import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
} from "@nexus/ui";

import { StatusPill } from "@/features/marketplace/components/orders-list/StatusMenu";
import { SCRIPT_REVIEW_ROUTE } from "@/features/marketplace/lib/preflight-report-path";

export interface PartnerPreflightSuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scriptReviewId: string;
  scriptTitle: string;
  clientName: string;
  submissionStatus: MarketplaceFulfillmentStatus;
  isPartialHookDelivery?: boolean;
}

export function PartnerPreflightSuccessDialog({
  open,
  onOpenChange,
  scriptReviewId,
  scriptTitle,
  clientName,
  submissionStatus,
  isPartialHookDelivery = false,
}: PartnerPreflightSuccessDialogProps) {
  const { t } = useTranslation();
  const primaryActionRef = useRef<HTMLAnchorElement>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md [&>[data-slot=dialog-close]]:top-2.5 [&>[data-slot=dialog-close]]:right-2.5 [&>[data-slot=dialog-close]]:size-11"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          primaryActionRef.current?.focus();
        }}
      >
        <DialogHeader className="pr-9">
          <div className="flex items-start gap-3">
            <div className="bg-success/10 text-success flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              <Icon name="check-circle" size="md" color="inherit" aria-hidden />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <DialogTitle className="[overflow-wrap:anywhere]">
                {isPartialHookDelivery
                  ? t(
                      "marketplace.partner.preflightHookUploadedTitle",
                      "Hook video uploaded for “{{scriptTitle}}”",
                      { scriptTitle },
                    )
                  : t(
                      "marketplace.partner.preflightSuccessTitle",
                      "“{{scriptTitle}}” submitted",
                      { scriptTitle },
                    )}
              </DialogTitle>
              <DialogDescription>
                {isPartialHookDelivery
                  ? t(
                      "marketplace.partner.preflightHookUploadedDescription",
                      "Upload a video for every remaining hook to submit this script. {{clientName}} is notified once all hooks are in.",
                      { clientName },
                    )
                  : t(
                      "marketplace.partner.preflightSuccessDescription",
                      "{{clientName}} and Adge operators will review this production version and share feedback if changes are needed.",
                      { clientName },
                    )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">
            {t("marketplace.partner.preflightSuccessStatus", "Status")}
          </span>
          <StatusPill status={submissionStatus} viewer="partner" />
        </div>

        <DialogFooter className="sm:justify-stretch">
          <Button asChild className="min-h-11 w-full">
            <Link
              ref={primaryActionRef}
              to={SCRIPT_REVIEW_ROUTE}
              params={{ reviewId: scriptReviewId }}
              onClick={() => onOpenChange(false)}
            >
              {t("marketplace.partner.preflightSuccessView", "View submission")}
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
